import {useNavigate, useParams} from "react-router-dom";
import * as React from "react";
import {FC, forwardRef, useImperativeHandle, useRef, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, Paper, Typography} from "@mui/material";
import {Control, FormProvider, useFieldArray, useForm, useFormContext} from "react-hook-form";
import {useApiHook} from "../../../utility/useApiHook";
import {
   BrandAppLocalizationsQuery,
   LocalizationInput,
   useBrandAppLocalizationsQuery,
   useBrandAppLocalizationsUpdateMutation,
   useBrandAppShopsQuery,
   useShopAppLocalizationsQuery,
   useShopAppLocalizationsUpdateMutation
} from "../../../Queries";
import {BodyText, TitleText} from "../../../layout/Typography";
import {Thumbnails} from "../../../components/Thumbnails";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import SettingsHeader from "../SettingsHeader";
import {SavingUI} from "../../../SavingProvider";
import {usePrompt} from "../../../components/useBlocker";
import {ShopTabs} from "../ShopTabs";
import UndoIcon from '@mui/icons-material/Undo';
import {LanguageSettingsPath} from "../languages/LanguageSettings";
import {LinkTo} from "../../../components/LinkTo";
import {ExportToExcel, ImportFromExcel, ImportResult} from "./TradeInAppTextExport";
import {Dialogs} from "../../../DialogProvider";

export type Section = {
   header: {
      title: string
      description: string
   }
   data: {
      key: string
      defaults: {
         [lang: string]: string
      }
   }[]
}

type DataValue = {
   text: string | undefined | null
   default: string | undefined | null
   inherited: boolean
};
type DataValues = {
   [id: string]: DataValue
};
export type TextFormData = {
   header: {
      title: string
      description: string
      images?: string[]
   }
   data: {
      key: string
      values: DataValues
      defaults: any
      duplicate?: boolean
      duplicateOf?: string
   }[]
}

export type ImportChange = {
   section: string
   key: string
   language: string
   oldValue: string
   newValue: string
}

type ImportPreviewDialogProps = {
   open: boolean
   changes: ImportChange[]
   onConfirm: () => void
   onCancel: () => void
}

const ImportPreviewDialog: FC<ImportPreviewDialogProps> = ({open, changes, onConfirm, onCancel}) => {
   // Group changes by section
   const changesBySection = changes.reduce((acc, change) => {
      if (!acc[change.section]) {
         acc[change.section] = [];
      }
      acc[change.section].push(change);
      return acc;
   }, {} as Record<string, ImportChange[]>);

   return (
      <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
         <DialogTitle>Review Import Changes</DialogTitle>
         <DialogContent dividers>
            {changes.length === 0 ? (
               <Typography color="textSecondary">No changes detected in the imported file.</Typography>
            ) : (
               <>
                  <Typography variant="body2" color="textSecondary" sx={{mb: 2}}>
                     {changes.length} change{changes.length !== 1 ? 's' : ''} will be applied:
                  </Typography>
                  <Box sx={{maxHeight: '400px', overflowY: 'auto'}}>
                     {Object.entries(changesBySection).map(([section, sectionChanges]) => (
                        <Box key={section} sx={{mb: 2}}>
                           <Typography variant="subtitle2" sx={{fontWeight: 'bold', mb: 1}}>
                              {section}
                           </Typography>
                           {sectionChanges.map((change, idx) => (
                              <Box key={idx} sx={{ml: 2, mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1}}>
                                 <Typography variant="body2" sx={{fontWeight: 500}}>
                                    {change.key} ({change.language.toUpperCase()})
                                 </Typography>
                                 <Box sx={{display: 'flex', gap: 1, mt: 0.5, fontSize: '0.875rem'}}>
                                    <Typography variant="body2" sx={{color: '#d32f2f', textDecoration: 'line-through', flex: 1, wordBreak: 'break-word'}}>
                                       {change.oldValue || '(empty)'}
                                    </Typography>
                                    <Typography variant="body2" sx={{color: '#666'}}>→</Typography>
                                    <Typography variant="body2" sx={{color: '#2e7d32', flex: 1, wordBreak: 'break-word'}}>
                                       {change.newValue || '(empty)'}
                                    </Typography>
                                 </Box>
                              </Box>
                           ))}
                        </Box>
                     ))}
                  </Box>
               </>
            )}
         </DialogContent>
         <DialogActions>
            <Button onClick={onCancel}>Cancel</Button>
            <Button onClick={onConfirm} variant="contained" disabled={changes.length === 0}>
               Apply {changes.length} Change{changes.length !== 1 ? 's' : ''}
            </Button>
         </DialogActions>
      </Dialog>
   );
}

const TradeInAppTextsPath = (brandId: string, shop: string) => {
   return "/brands/" + brandId + "/settings/translations/" + shop
}

export const TradeInAppTexts = () => {
   const {brandHouseId} = useParams<{ brandHouseId: string }>();
   const headers: Section[] = useApiHook('https://core.create2stay.com/locheaders2');
   if (!headers) {
      return <LinearProgress/>
   }
   return <div>
      <TradeInAppTextsInner headers={headers} brandHouseId={brandHouseId}/>
   </div>
}

type TradeInAppTextsInnerProps = {
   headers: Section[]
   brandHouseId: string
}

export const TradeInAppTextsInner = ({headers, brandHouseId}: TradeInAppTextsInnerProps) => {
   const {edit} = useParams<{ edit: string }>();
   const {data} = useBrandAppShopsQuery({
      variables: {id: brandHouseId}
   });
   const navigate = useNavigate();
   const ref = useRef<{ onExport: () => void, previewImport: (data: TextFormData[]) => void }>();
   const importRef = useRef<HTMLInputElement | null>(null)

   if (!data?.Shops) {
      return <LinearProgress/>
   }

   const onStartImport = () => {
      Dialogs.confirm({
         title: 'Import translations?',
         subtitle: 'The imported translations will be imported into this view, and you will be able to review the changes before you save them.'
      }, () => {
         importRef.current?.click()
      })
   }

   const onSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target?.files?.[0];
      if (file) {
         let result: ImportResult | null = null;
         await SavingUI.process(async () => {
            result = await ImportFromExcel(file);
         }, 'Importing translations ... please wait')

         // Reset file input so same file can be selected again
         if (importRef.current) {
            importRef.current.value = '';
         }

         if (result && result.duplicateKeys.length > 0) {
            const keyList = result.duplicateKeys.slice(0, 5).join(', ');
            const moreText = result.duplicateKeys.length > 5 ? ` and ${result.duplicateKeys.length - 5} more` : '';
            Dialogs.info({
               title: 'Duplicate keys detected',
               subtitle: `The imported file contains keys that appear in multiple sections: ${keyList}${moreText}. Please remove duplicate rows from the Excel file and try again. Only the first occurrence of each key should be present.`
            });
            return;
         }

         if (result) {
            ref.current?.previewImport(result.data)
         }
      }
   }

   const handleChange = (newValue: string) => {
      navigate(TradeInAppTextsPath(brandHouseId, newValue));
   };

   return <div>
      <SettingsHeader title={"Translations"} subtitle={
         <BodyText type={"subtitle1"}>
            Define texts for <LinkTo to={LanguageSettingsPath(brandHouseId, edit)}>languages</LinkTo> here
         </BodyText>
      }>
         <input ref={importRef}
                type="file"
                id="importInputFile"
                style={{display: 'none'}}
                onChange={onSelectFile}
         />

         <Button onClick={onStartImport}>Import</Button>
         <Button onClick={() => ref.current?.onExport()}>Export</Button>
         <Button type="submit" form={"appTextForm"} variant={"contained"}>Save</Button>
      </SettingsHeader>
      <ShopTabs
         activeId={edit}
         brandHouseId={brandHouseId}
         onChange={handleChange}
         renderDefault={() => <TradeInAppTextsEditor ref={ref} brandHouseId={brandHouseId} headers={headers}/>}
         renderShop={(shop) => <TradeInAppTextsEditorShop key={'appText_shop_' + shop} ref={ref} shopId={shop}
                                                          headers={headers}/>}
      />
   </div>
}

export type TradeInAppTextsEditorProps = {
   brandHouseId?: string
   shopId?: string
   headers: Section[]
}

export const TradeInAppTextsEditor = forwardRef(({brandHouseId, headers}: TradeInAppTextsEditorProps, ref) => {
   const {data} = useBrandAppLocalizationsQuery({
      variables: {
         id: brandHouseId,
         type: 'tradein2'
      },
      fetchPolicy: 'no-cache'
   });

   const [saveLocalizations] = useBrandAppLocalizationsUpdateMutation();

   if (!data) {
      return <LinearProgress/>
   }

   const onSave = async (data: LocalizationInput[]) => {
      await SavingUI.process(async () => {
         await saveLocalizations({
            variables: {
               id: brandHouseId,
               input: data,
               type: 'tradein2'
            },
            refetchQueries: ['BrandLocalizations']
         });
      }, 'Default translations saved')
   }

   let brandLocales = data.BrandSettings?.locales || [];
   const locales: string[] = brandLocales
      .map(locale => locale?.locale) as string[];

   const localizationData = convertToFormData(headers, data, true);

   return <TradeInAppEditor ref={ref} localizations={localizationData} languages={locales} onSave={onSave}
                            isDefault={true}/>
})

export const TradeInAppTextsEditorShop = forwardRef(({shopId, headers}: TradeInAppTextsEditorProps, ref) => {
   const {data} = useShopAppLocalizationsQuery({
      variables: {
         id: shopId,
         type: 'tradein2'
      },
      fetchPolicy: 'no-cache'
   });

   const [saveLocalizations] = useShopAppLocalizationsUpdateMutation();

   if (!data) {
      return <LinearProgress/>
   }

   const onSave = async (data: LocalizationInput[]) => {
      await SavingUI.process(async () => {
         await saveLocalizations({
            variables: {
               id: shopId,
               input: data,
               type: 'tradein2'
            },
            refetchQueries: ['BrandLocalizations']
         });
      }, 'Translations saved')
   }

   let brandLocales = data.BrandSettings?.locales || [];
   const locales: string[] = brandLocales
      .map(locale => locale?.locale) as string[];

   const localizationData = convertToFormData(headers, data, false);

   return <TradeInAppEditor ref={ref} localizations={localizationData} languages={locales} onSave={onSave}
                            isDefault={false}/>
})

type TradeInAppEditorProps = {
   languages: string[]
   localizations: TextFormData[]
   onSave: (data: LocalizationInput[]) => Promise<void>
   isDefault: boolean
}

export type TradeInAppFormData = {
   sections: TextFormData[]
}

const TradeInAppEditor = forwardRef(({languages, localizations, onSave, isDefault}: TradeInAppEditorProps, ref) => {
   const methods = useForm<TradeInAppFormData>({
      defaultValues: {
         sections: localizations
      }
   });
   const {handleSubmit, register, control, getValues, reset, setValue, formState: {isDirty}} = methods;

   // Import preview state
   const [previewOpen, setPreviewOpen] = useState(false);
   const [previewChanges, setPreviewChanges] = useState<ImportChange[]>([]);
   const [pendingImportData, setPendingImportData] = useState<TextFormData[] | null>(null);

   const onSubmit = async (data: TradeInAppFormData) => {
      const updates: LocalizationInput[] = [];
      for (const section of data.sections) {
         for (const datum of section.data) {
            for (const language of languages) {
               if (isDefault && datum.values[language].text !== datum.defaults[language]) {
                  updates.push({
                     key: datum.key,
                     language: language,
                     value: datum.values[language].text
                  })
               } else if (!isDefault && datum.values[language].text !== datum.values[language].default) {
                  updates.push({
                     key: datum.key,
                     language: language,
                     value: datum.values[language].text
                  })
               }
            }
         }
      }
      await onSave(updates);
   }

   // Compute differences between current values and imported data
   const computeImportChanges = (importedData: TextFormData[]): ImportChange[] => {
      const changes: ImportChange[] = [];
      const currentValues = getValues().sections;

      for (const currentSection of currentValues) {
         const importedSection = importedData.find(dat => dat.header.title === currentSection.header.title);
         if (!importedSection) continue;

         for (const currentData of currentSection.data) {
            // Skip duplicates - they shouldn't be imported
            if (currentData.duplicate) continue;

            const importedItem = importedSection.data.find(dat => dat.key === currentData.key);
            if (!importedItem) continue;

            for (const language of languages) {
               const currentValue = currentData.values[language]?.text ?? '';
               const importedValue = importedItem.values[language]?.text ?? '';

               // Trim both values for comparison - Excel strips whitespace from cells
               const currentTrimmed = currentValue.trim();
               const importedTrimmed = importedValue.trim();

               if (currentTrimmed !== importedTrimmed) {
                  changes.push({
                     section: currentSection.header.title,
                     key: currentData.key,
                     language: language,
                     oldValue: currentValue,
                     newValue: importedValue
                  });
               }
            }
         }
      }

      return changes;
   };

   // Apply the pending import data to the form
   const applyImport = () => {
      if (!pendingImportData) return;

      const currentValues = getValues().sections;

      // Apply changes directly to each field
      for (let sectionIdx = 0; sectionIdx < currentValues.length; sectionIdx++) {
         const currentSection = currentValues[sectionIdx];
         const updated = pendingImportData.find(dat => dat.header.title === currentSection.header.title);
         if (!updated) continue;

         for (let dataIdx = 0; dataIdx < currentSection.data.length; dataIdx++) {
            const currentData = currentSection.data[dataIdx];
            // Skip duplicates
            if (currentData.duplicate) continue;

            const updatedData = updated.data.find(dat => dat.key === currentData.key);
            if (updatedData) {
               for (const language of languages) {
                  if (currentData.values[language] && updatedData.values[language]) {
                     const newText = updatedData.values[language].text;
                     const defaultText = currentData.values[language].default;
                     const isInherited = newText === defaultText;

                     setValue(
                        `sections.${sectionIdx}.data.${dataIdx}.values.${language}.text`,
                        newText,
                        { shouldDirty: true }
                     );
                     setValue(
                        `sections.${sectionIdx}.data.${dataIdx}.values.${language}.inherited`,
                        isInherited,
                        { shouldDirty: true }
                     );
                  }
               }
            }
         }
      }

      // Clean up
      setPreviewOpen(false);
      setPendingImportData(null);
      setPreviewChanges([]);
   };

   const cancelImport = () => {
      setPreviewOpen(false);
      setPendingImportData(null);
      setPreviewChanges([]);
   };

   useImperativeHandle(ref, () => ({
      async onExport() {
         await ExportToExcel(languages, getValues().sections)
      },
      previewImport(data: TextFormData[]) {
         const changes = computeImportChanges(data);
         setPreviewChanges(changes);
         setPendingImportData(data);
         setPreviewOpen(true);
      }
   }))

   const {fields} = useFieldArray({
      control,
      name: "sections"
   });

   React.useEffect(() => {
      register("sections");
   }, [register]);


   usePrompt('Discard changes?', isDirty);

   return <FormProvider {...methods}>
      <form id={"appTextForm"} onSubmit={handleSubmit(onSubmit)}/>
      {fields.map((section, index) => {
         return <Paper key={'appTexts_section_' + section.header.title} sx={{padding: '20px', marginBottom: '20px'}}>
            <Box sx={{flexDirection: 'row', display: 'flex'}} justifyContent={"space-between"}>
               <div>
                  <TitleText type={"h2"}>{section.header.title}</TitleText>
                  <BodyText type={"subtitle1"}>{section.header.description}</BodyText>
               </div>
               {section.header.images && <Thumbnails imageUrls={section.header.images} size={60}/>}
            </Box>
            <div style={{marginTop: '10px'}}>
               <SectionEditor languages={languages} control={control} index={index} isDefault={isDefault}/>
            </div>
         </Paper>
      })}
      <ImportPreviewDialog
         open={previewOpen}
         changes={previewChanges}
         onConfirm={applyImport}
         onCancel={cancelImport}
      />
   </FormProvider>
});

type SectionEditorProps = {
   languages: string[]
   control: Control<TradeInAppFormData>
   index: number
   isDefault: boolean
}

const SectionEditor: FC<SectionEditorProps> = ({languages, control, index, isDefault}) => {
   const {fields} = useFieldArray<TradeInAppFormData>({
      control,
      name: `sections.${index}.data`
   });
   const {getValues, setValue, trigger} = useFormContext();

   return <>{fields.map((field, nested) => {
      let item = field as any
      const isDuplicate = item.duplicate === true;
      const duplicateOf = item.duplicateOf as string | undefined;

      return <Grid key={'appTexts_section_editor_' + field.id} justifyContent={'flex-start'} wrap={'nowrap'}
                   style={{gap: '10px'}} container>
         {languages.map(lang => {
            const undo = <UndoIcon titleAccess={"Revert changes to default"} style={{cursor: 'pointer'}}
                                   fontSize={"small"} onClick={async () => {
               const defaultValue = getValues(`sections.${index}.data.${nested}.values.${lang}.default`);
               setValue(`sections.${index}.data.${nested}.values.${lang}.text`, defaultValue);
               setValue(`sections.${index}.data.${nested}.values.${lang}.inherited`, true);
               await trigger(`sections.${index}.data.${nested}.values.${lang}`)
            }}/>

            const inherited = getValues(`sections.${index}.data.${nested}.values.${lang}.inherited`)

            if (isDuplicate) {
               return <FormInputText
                  key={`sections.${index}.data.${nested}.values.${lang}.text`}
                  name={`sections.${index}.data.${nested}.values.${lang}.text`}
                  control={control}
                  label={item.key + '(' + lang.toUpperCase() + ')'}
                  disabled={true}
                  helperText={`Duplicate - edit in "${duplicateOf}"`}
                  style={{
                     backgroundColor: '#ffebee'
                  }}
               />
            }

            return <FormInputText
               key={`sections.${index}.data.${nested}.values.${lang}.text`}
               name={`sections.${index}.data.${nested}.values.${lang}.text`}
               control={control}
               label={item.key + '(' + lang.toUpperCase() + ')'}
               InputProps={{
                  endAdornment: <div>{!inherited && undo}</div>
               }}
               style={{
                  backgroundColor: inherited ? '#fcf4f4' : '#ffffff'
               }}
            />
         })}
      </Grid>
   })}</>
}

function convertToFormData(headers: Section[], data: BrandAppLocalizationsQuery, isDefault: boolean): TextFormData[] {
   let t0 = Date.now()
   try {
      const seenKeys: Map<string, string> = new Map(); // key -> first section title

      const sortedHeaders = headers.sort((a, b) => {
         if (a.header.title < b.header.title) {
            return -1;
         } else if (a.header.title > b.header.title) {
            return 1;
         } else {
            return 0;
         }
      });

      return sortedHeaders.map(header => {
         return {
            header: header.header,
            data: header.data.map(sect => {
               const actualLocalizations = data.BrandLocalizations?.filter(loc => {
                  return loc?.key === sect.key
               });
               let values: DataValues = {};
               actualLocalizations?.forEach(key => {
                  if (key?.language) {
                     values[key.language] = {
                        text: key.value,
                        inherited: key.inherited,
                        default: key.default
                     };
                  }
               });
               Object.keys(sect.defaults).forEach(def => {
                  if (!values[def]) {
                     values[def] = {
                        text: sect.defaults[def],
                        inherited: true,
                        default: sect.defaults[def]
                     }
                  } else {
                     if (isDefault) {
                        values[def].default = sect.defaults[def]
                     } // otherwise the default comes from the brand
                  }
               });

               // Track duplicate keys across sections
               const isDuplicate = seenKeys.has(sect.key);
               const duplicateOf = isDuplicate ? seenKeys.get(sect.key) : undefined;
               if (!isDuplicate) {
                  seenKeys.set(sect.key, header.header.title);
               }

               return {
                  key: sect.key,
                  values: values,
                  defaults: sect.defaults,
                  duplicate: isDuplicate,
                  duplicateOf: duplicateOf
               }
            })
         }
      });
   } finally {
      console.log(Date.now() - t0 + ' ms converting data')
   }
}